/** Default copy shown when a Firebase auth error has no specific message. */
export const GENERIC_SIGN_IN_ERROR = "Sign in failed. Please try again.";

/** Default copy shown when sending the email sign-in link fails. */
export const SEND_LINK_FAILED_MESSAGE =
  "We couldn't send your sign-in link. Please try again.";

/** Copy for a failed provider link. The sign-in itself succeeded. */
export const LINK_FAILED_MESSAGE =
  "We couldn't connect your other sign-in method. You're signed in.";

/** Default copy when redeeming an email sign-in link fails for an unmapped reason. */
export const REDEEM_LINK_FAILED_MESSAGE =
  "We couldn't finish signing you in with that link. Please request a new one.";

/** Copy for a session that was minted but whose account didn't load. */
export const SESSION_NOT_LOADED_MESSAGE =
  "You're signed in, but we couldn't load your account. Please refresh the page.";

/** User-facing copy per Firebase `auth/*` error code. */
const FIREBASE_AUTH_MESSAGES = {
  "auth/account-exists-with-different-credential":
    "That email is already on Stanza with a different sign-in method. Try another option below.",
  "auth/credential-already-in-use":
    "That account is already connected to another Stanza account.",
  "auth/internal-error": "Something went wrong on our side. Please try again.",
  "auth/invalid-continue-uri":
    "Sign-in isn't available from this page. Please try again from stanzacal.com.",
  "auth/invalid-credential":
    "We couldn't verify that account. Please try signing in again.",
  "auth/invalid-email":
    "That email address doesn't look right. Check it and try again.",
  "auth/network-request-failed":
    "We couldn't reach the network. Check your connection and try again.",
  "auth/operation-not-allowed":
    "That sign-in method isn't available right now. Try another option below.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in window. Allow pop-ups, or use the email link below.",
  "auth/quota-exceeded":
    "We can't send sign-in emails right now. Try another sign-in option, or try again later.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a moment and try again.",
  "auth/unauthorized-continue-uri":
    "Sign-in isn't available from this page. Please try again from stanzacal.com.",
  "auth/unauthorized-domain":
    "Sign-in isn't available from this address. Please try again from stanzacal.com.",
  "auth/user-disabled":
    "This account has been disabled. Contact support@stanza.co for help.",
};

/** Copy for codes whose meaning differs when redeeming an email link. */
const EMAIL_LINK_REDEEM_MESSAGES = {
  "auth/expired-action-code":
    "The sign-in link has expired. Please request a new one.",
  "auth/invalid-action-code":
    "The sign-in link is invalid. It may have already been used.",
  "auth/invalid-email":
    "We couldn't use that email address with this link. Please request a new one.",
};

/** User-facing copy per Stanza-owned pseudo error code. */
const STANZA_ERROR_MESSAGES = {
  "stanza/link-account-mismatch":
    "You signed in with a different account, so the other sign-in method wasn't connected to it.",
};

/** Link failures whose code tells the user something beyond "it didn't connect". */
const LINK_SPECIFIC_CODES = [
  "auth/credential-already-in-use",
  "stanza/link-account-mismatch",
];

/**
 * Resolves the error code and user-facing message from an Axios error,
 * handling both the legacy `{ message }` backend shape and the new
 * `{ error: { code, message } }` shape.
 *
 * @param {import("axios").AxiosError} error
 * @param {Record<string, string>} codesByMessage - maps raw backend message strings to error code keys
 * @param {Record<string, { message: string }>} shapes - maps error code keys to display-ready metadata
 * @returns {{ code?: string, message?: string }}
 */
export function resolveBackendError(error, codesByMessage = {}, shapes = {}) {
  const has = (map, key) =>
    key != null && Object.prototype.hasOwnProperty.call(map, key);
  const responseError = error?.response?.data?.error;
  const backendMessage =
    responseError?.message ?? error?.response?.data?.message;
  const code =
    responseError?.code ??
    (has(codesByMessage, backendMessage) ? codesByMessage[backendMessage] : undefined);
  const shapeMessage = has(shapes, code) ? shapes[code].message : undefined;
  return { code, message: shapeMessage ?? backendMessage };
}

/**
 * Detects Axios failures caused by the browser aborting/canceling the XHR.
 * Axios timeouts also use ECONNABORTED, so keep this scoped to the browser
 * abort message instead of suppressing every ECONNABORTED failure.
 *
 * @param {import("axios").AxiosError} error
 * @returns {boolean}
 */
export function isAxiosRequestAbort(error) {
  return (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.message === "canceled" ||
    (error?.code === "ECONNABORTED" && error?.message === "Request aborted")
  );
}

/**
 * Resolves user-facing copy for a Firebase auth error, never exposing the
 * SDK's own `Firebase: Error (auth/...)` message.
 *
 * @param {{ code?: string }} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function firebaseAuthErrorMessage(
  error,
  fallback = GENERIC_SIGN_IN_ERROR
) {
  const { code } = error ?? {};
  const has = (map) => Object.prototype.hasOwnProperty.call(map, code);
  if (has(FIREBASE_AUTH_MESSAGES)) return FIREBASE_AUTH_MESSAGES[code];
  if (has(STANZA_ERROR_MESSAGES)) return STANZA_ERROR_MESSAGES[code];
  return fallback;
}

/**
 * Resolves copy for a failed provider link. Sign-in codes like
 * `auth/invalid-credential` would read as a sign-in failure, so only
 * link-specific codes get their own message.
 *
 * @param {{ code?: string }} error
 * @returns {string}
 */
export function firebaseLinkErrorMessage(error) {
  return LINK_SPECIFIC_CODES.includes(error?.code)
    ? firebaseAuthErrorMessage(error)
    : LINK_FAILED_MESSAGE;
}

/**
 * Resolves copy for a failed email-link redemption.
 *
 * @param {{ code?: string }} error
 * @returns {string}
 */
export function emailLinkRedeemErrorMessage(error) {
  const code = error?.code;
  if (Object.prototype.hasOwnProperty.call(EMAIL_LINK_REDEEM_MESSAGES, code)) {
    return EMAIL_LINK_REDEEM_MESSAGES[code];
  }
  return firebaseAuthErrorMessage(error, REDEEM_LINK_FAILED_MESSAGE);
}
